import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Row,
  Col,
  Button,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
  Table,
  Input,
  FormGroup,
  FormFeedback,
} from "reactstrap";
import Select from "react-select";
import { FaFileDownload, FaFileUpload, FaPen } from "react-icons/fa";
import fileDownload from "js-file-download";

import api from "services/api";
import { useToast } from "hooks/toast";
import { useCompanies } from "hooks/companies";
import FileUpload from "components/Input/fileUpload";
import SpecificEmpresaRequiredNotice from "components/SpecificEmpresaRequiredNotice";
import PageHero, { PageHeroRefreshButton } from "components/PageHero";
import FilterBand from "components/FilterBand";
import SortHeader from "components/SortHeader";
import TablePagination from "components/TablePagination";
import { toggleSort, sortRows, paginate } from "utils/tableHelpers";
import { reactSelectStyles, reactSelectPortalProps } from "utils/reactSelectStyles";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import ShellCard, { TableSurface } from "components/ShellCard";

/**
 * Página compartilhada de Tributos (PO1-2729 / PO1-2730, épico PO1-2723).
 * Tributos Estaduais e Municipais são a mesma tela com colunas/endpoints
 * diferentes; a configuração vem de ./configs.js e os wrappers em
 * views/Admin/TaxRatesState e TaxRatesCounty só a injetam.
 *
 * Config: { slug, eyebrow, title, description, urls: { list, exportCsv, importCsv },
 *           columns: [{ key, label, align?, render?(row), sortValue?(row) }],
 *           searchKeys: [key], filters: [{ key, label, options?(rows) | boolean }],
 *           exportFileName }
 */

const ALL = "__ALL__";

const stateBoxStyle = {
  textAlign: "center",
  padding: "2.5rem 1rem",
  color: AUTOPILOT_COLORS.muted,
};

const stateTitleStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "0.98rem",
  fontWeight: 700,
  marginBottom: "0.35rem",
};

const filterLabelStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.76rem",
  marginBottom: "0.35rem",
  display: "block",
};

function normalizeText(value) {
  return String(value === null || value === undefined ? "" : value)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

function distinctOptions(rows, key) {
  const seen = {};
  const values = [];
  (rows || []).forEach((row) => {
    const raw = row ? row[key] : undefined;
    if (raw === null || raw === undefined || raw === "") return;
    const str = String(raw);
    if (seen[str]) return;
    seen[str] = true;
    values.push(str);
  });
  values.sort((a, b) => a.localeCompare(b, "pt-BR", { numeric: true }));
  return values.map((v) => ({ value: v, label: v }));
}

const BOOLEAN_OPTIONS = [
  { value: "true", label: "Ativo" },
  { value: "false", label: "Inativo" },
];

export default function TaxRatesPage({
  slug,
  eyebrow = "Impostos",
  title,
  description,
  urls,
  columns,
  searchKeys,
  searchLabel = "Buscar",
  searchHint,
  searchPlaceholder = "Buscar...",
  filters = [],
  exportFileName,
  editFields = [],
}) {
  const [data, setData] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterValues, setFilterValues] = useState({});
  const [sort, setSort] = useState({ field: columns[0] ? columns[0].key : "", dir: "asc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { addToast } = useToast();
  const { empresa, hasEmpresaSelection } = useCompanies();
  const empresaId = empresa?.value;

  const loadData = useCallback(async () => {
    if (!hasEmpresaSelection) {
      setData([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setLoadError(null);
      const result = await api.get(urls.list, {
        params: { idEmbarcadorRemetente: empresaId },
      });
      if (result.status === 200) {
        setData(Array.isArray(result.data) ? result.data : []);
      }
    } catch (error) {
      setData([]);
      setLoadError("Não foi possível carregar os tributos deste embarcador.");
    } finally {
      setLoading(false);
    }
  }, [empresaId, hasEmpresaSelection, urls.list]);

  // Recarrega ao trocar de embarcador e ao fechar o modal de envio (mesma
  // regra da tela antiga: upload concluído = lista atualizada).
  useEffect(() => {
    if (modal === false) {
      setData([]);
      loadData();
    }
  }, [loadData, modal]);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const config = {
        headers: {
          "content-type": "application/octet-stream",
          "Content-Disposition": "attachment;filename=feriados.csv",
        },
      };
      const result = await api.get(
        urls.exportCsv,
        {
          params: { idEmbarcadorRemetente: empresaId },
        },
        config
      );
      if (result.status === 200) {
        fileDownload(result.data, exportFileName || "filename.csv");
      }
    } catch (error) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Error",
        description: "Erro ao baixar planilha!",
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setDownloading(false);
    }
  };

  const toggleModal = useCallback(() => {
    setModal((v) => !v);
  }, []);

  const openEdit = useCallback(
    (row) => {
      const initial = {};
      editFields.forEach((f) => {
        initial[f.key] = f.type === "boolean" ? Boolean(row[f.key]) : row[f.key];
      });
      setEditForm(initial);
      setEditErrors({});
      setEditing(row);
    },
    [editFields]
  );

  const closeEdit = useCallback(() => {
    if (saving) return;
    setEditing(null);
  }, [saving]);

  const updateEditField = useCallback((key, value) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSaveEdit = useCallback(async () => {
    const errors = {};
    editFields.forEach((f) => {
      if (f.type !== "percent") return;
      const raw = editForm[f.key];
      const num = Number(raw);
      if (raw === "" || raw === null || raw === undefined || Number.isNaN(num)) {
        errors[f.key] = "Informe um valor.";
      } else if (num < 0 || num > 100) {
        errors[f.key] = "Deve ser entre 0 e 100.";
      }
    });
    if (Object.keys(errors).length > 0) {
      setEditErrors(errors);
      return;
    }

    const payload = { idEmbarcador: empresaId };
    editFields.forEach((f) => {
      payload[f.key] = f.type === "percent" ? Number(editForm[f.key]) : Boolean(editForm[f.key]);
    });

    try {
      setSaving(true);
      const result = await api.put(urls.update(editing.id), payload);
      setData((prev) => prev.map((row) => (row.id === editing.id ? { ...row, ...result.data } : row)));
      addToast({
        place: "tr",
        type: "success",
        title: "Tributo atualizado",
        description: "As alterações foram salvas.",
        icon: "tim-icons icon-check-2",
      });
      setEditing(null);
    } catch (error) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro",
        description: error?.response?.data?.error || "Erro ao salvar o tributo.",
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setSaving(false);
    }
  }, [addToast, editForm, editFields, editing, empresaId, urls]);

  const filterOptions = useMemo(() => {
    const map = {};
    filters.forEach((f) => {
      map[f.key] = f.boolean ? BOOLEAN_OPTIONS : distinctOptions(data, f.key);
    });
    return map;
  }, [data, filters]);

  const filtered = useMemo(() => {
    const query = normalizeText(search);
    return (Array.isArray(data) ? data : []).filter((row) => {
      for (let i = 0; i < filters.length; i += 1) {
        const f = filters[i];
        const selected = filterValues[f.key];
        if (!selected || selected === ALL) continue;
        const raw = row ? row[f.key] : undefined;
        if (f.boolean) {
          if (String(Boolean(raw)) !== selected) return false;
        } else if (String(raw === null || raw === undefined ? "" : raw) !== selected) {
          return false;
        }
      }
      if (query) {
        const index = normalizeText(
          (searchKeys || []).map((k) => (row ? row[k] : "")).filter(Boolean).join(" ")
        );
        if (!index.includes(query)) return false;
      }
      return true;
    });
  }, [data, filters, filterValues, search, searchKeys]);

  const sortValue = useCallback(
    (row, field) => {
      const col = columns.find((c) => c.key === field);
      if (col && typeof col.sortValue === "function") return col.sortValue(row);
      const raw = row ? row[field] : undefined;
      if (typeof raw === "boolean") return raw ? 1 : 0;
      if (typeof raw === "string" && raw.trim() !== "" && !Number.isNaN(Number(raw))) return Number(raw);
      return raw;
    },
    [columns]
  );

  const sorted = useMemo(() => sortRows(filtered, sort, sortValue), [filtered, sort, sortValue]);
  const paged = useMemo(() => paginate(sorted, page, pageSize), [sorted, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [search, filterValues, sort, pageSize]);

  const handleSort = useCallback((field) => setSort((prev) => toggleSort(prev, field)), []);

  const activeFiltersCount = filters.filter((f) => filterValues[f.key] && filterValues[f.key] !== ALL).length;
  const hasFilters = normalizeText(search) !== "" || activeFiltersCount > 0;

  const clearFilters = useCallback(() => {
    setSearch("");
    setFilterValues({});
  }, []);

  if (!hasEmpresaSelection) {
    return (
      <div className={`content ${slug}-page`}>
        <SpecificEmpresaRequiredNotice />
      </div>
    );
  }

  const renderState = () => {
    if (loading) {
      return (
        <div style={stateBoxStyle}>
          <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent, marginRight: "0.5rem" }} />
          Carregando tributos…
        </div>
      );
    }
    if (loadError) {
      return (
        <div style={stateBoxStyle}>
          <div style={stateTitleStyle}>Não foi possível carregar</div>
          <p style={{ marginBottom: "0.75rem" }}>{loadError}</p>
          <Button size="sm" color="info" outline onClick={loadData}>
            Tentar novamente
          </Button>
        </div>
      );
    }
    if (sorted.length === 0) {
      return (
        <div style={stateBoxStyle}>
          <div style={stateTitleStyle}>
            {hasFilters ? "Nenhum tributo encontrado" : "Nenhum dado a ser exibido"}
          </div>
          <p style={{ marginBottom: 0 }}>
            {hasFilters
              ? "Ajuste os filtros ou limpe a busca."
              : "Envie a planilha para cadastrar os tributos deste embarcador."}
          </p>
        </div>
      );
    }
    return null;
  };

  const state = renderState();

  return (
    <div className={`content ${slug}-page`}>
      <Modal isOpen={modal} toggle={toggleModal} modalClassName="modal-align-top" fade={false}>
        <ModalHeader toggle={toggleModal} tag="h5">
          Enviar planilha
        </ModalHeader>
        <ModalBody className="text-center">
          <Row>
            <Col className="text-center">
              <FileUpload
                api={api}
                url={urls.importCsv}
                params={{ idEmbarcadorRemetente: empresaId }}
                paramsFormData={null}
                accept=".csv"
                filekey="file"
              />
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      <Modal isOpen={!!editing} toggle={closeEdit} modalClassName="modal-align-top" fade={false} size="sm">
        <ModalHeader toggle={closeEdit} tag="h5">
          Editar tributo
        </ModalHeader>
        <ModalBody>
          {editFields.map((f) => (
            <FormGroup key={f.key}>
              <Label for={`${slug}-edit-${f.key}`}>{f.label}</Label>
              {f.type === "boolean" ? (
                <Input
                  id={`${slug}-edit-${f.key}`}
                  type="select"
                  value={editForm[f.key] ? "true" : "false"}
                  onChange={(e) => updateEditField(f.key, e.target.value === "true")}
                  disabled={saving}
                >
                  <option value="true">{f.trueLabel || "Sim"}</option>
                  <option value="false">{f.falseLabel || "Não"}</option>
                </Input>
              ) : (
                <>
                  <Input
                    id={`${slug}-edit-${f.key}`}
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={editForm[f.key] === undefined || editForm[f.key] === null ? "" : editForm[f.key]}
                    onChange={(e) => updateEditField(f.key, e.target.value)}
                    invalid={Boolean(editErrors[f.key])}
                    disabled={saving}
                  />
                  {editErrors[f.key] ? <FormFeedback>{editErrors[f.key]}</FormFeedback> : null}
                </>
              )}
            </FormGroup>
          ))}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
            <Button color="default" outline size="sm" onClick={closeEdit} disabled={saving}>
              Cancelar
            </Button>
            <Button color="info" size="sm" onClick={handleSaveEdit} disabled={saving}>
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <PageHero
        eyebrow={eyebrow}
        title={title}
        description={description}
        actions={
          <>
            <PageHeroRefreshButton onClick={loadData} loading={loading} />
            <Button color="success" className="btn-sm" onClick={handleDownload} disabled={downloading || loading}>
              {downloading ? <Spinner size="sm" /> : <FaFileDownload style={{ marginRight: 6 }} />}
              Baixar planilha
            </Button>
            <Button color="info" className="btn-sm" onClick={toggleModal}>
              <FaFileUpload style={{ marginRight: 6 }} />
              Enviar planilha
            </Button>
          </>
        }
      />

      <FilterBand
        mode="client"
        searchLabel={searchLabel}
        searchHint={searchHint}
        searchPlaceholder={searchPlaceholder}
        searchValue={search}
        onSearchChange={setSearch}
        activeFiltersCount={activeFiltersCount}
        panelTitle="Filtros"
        panelDescription="Refine a lista carregada."
        onClear={hasFilters ? clearFilters : undefined}
        showActions={hasFilters}
      >
        <Row>
          {filters.map((f) => {
            const options = filterOptions[f.key] || [];
            const current = options.find((o) => o.value === filterValues[f.key]) || null;
            return (
              <Col md="4" lg="3" key={f.key} style={{ marginBottom: "0.75rem" }}>
                <Label for={`${slug}-filtro-${f.key}`} style={filterLabelStyle}>
                  {f.label}
                </Label>
                <Select
                  inputId={`${slug}-filtro-${f.key}`}
                  classNamePrefix="react-select"
                  styles={reactSelectStyles}
                  {...reactSelectPortalProps()}
                  options={options}
                  value={current}
                  placeholder="Todos"
                  isClearable
                  onChange={(opt) =>
                    setFilterValues((prev) => ({ ...prev, [f.key]: opt ? opt.value : ALL }))
                  }
                />
              </Col>
            );
          })}
        </Row>
      </FilterBand>

      <ShellCard
        className={`${slug}-lista`}
        title="Tributos cadastrados"
        description={`${sorted.length} resultado(s)`}
      >
          {state ? (
            state
          ) : (
            <TableSurface>
              <Table size="sm" responsive hover className={`${slug}-table hub-thead-accent`}>
                <thead className="text-primary">
                  <tr>
                    {columns.map((col) => (
                      <th key={col.key} className={col.align === "right" ? "text-right" : undefined}>
                        <SortHeader
                          label={col.label}
                          field={col.key}
                          sort={sort}
                          onSort={handleSort}
                          align={col.align}
                        />
                      </th>
                    ))}
                    {editFields.length > 0 && <th className="text-right"> </th>}
                  </tr>
                </thead>
                <tbody>
                  {paged.items.map((row, index) => (
                    <tr key={row.id !== undefined ? row.id : `${paged.page}-${index}`}>
                      {columns.map((col) => (
                        <td key={col.key} className={col.align === "right" ? "text-right" : undefined}>
                          {typeof col.render === "function"
                            ? col.render(row)
                            : row[col.key] === null || row[col.key] === undefined || row[col.key] === ""
                            ? "—"
                            : String(row[col.key])}
                        </td>
                      ))}
                      {editFields.length > 0 && (
                        <td className="text-right">
                          <Button
                            color="link"
                            size="sm"
                            className="btn-icon"
                            title="Editar"
                            disabled={row.id === undefined}
                            onClick={() => openEdit(row)}
                          >
                            <FaPen />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
              <TablePagination
                page={paged.page}
                pageSize={pageSize}
                total={paged.total}
                itemLabel="tributo(s)"
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </TableSurface>
          )}
      </ShellCard>
    </div>
  );
}
